import { isFulfilled } from "@reduxjs/toolkit";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import DefaultImg from "../../../assets/img/default-image.svg";
import {
  ITEMS_PER_PAGE_OPTIONS,
  MAX_VISIBLE_PAGES,
} from "../../../constants/app";
import { profileSectionTitle } from "../../../models/vacancy";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { resetReferooDocumentsAction } from "../../../store/superadmin/slice";
import { getCandidateData, getReferooDocumentThunk, resendVerificationEmailThunk } from "../../../store/superadmin/thunks";
import {
  ICandidateData,
  PaginationLink,
} from "../../../store/superadmin/types";
import { capitalizeName, extractSuburbFromAddress } from "../../../utils/stringUtils";
import Pagination from "../../Pagination/Pagination";
import PlaceHolderImage from "../../UI/Icons/PlaceHolderImage";
import CandidateReviewModal from "./CandidateReviewModal/Index";

/**
 * Props for the AllCandidates component.
 *
 */
interface AllCandidatesProps {
  /** {ICandidateData} candidatesList - The list of candidate data to be displayed.*/
  candidatesList: ICandidateData;
  /** {(value: number) => void} setItemPerPageList - Function to set the number of items displayed per page. */
  setItemPerPageList: (value: number) => void;
  /*  {(value: number) => void} setCurrentPendingPage - Function to update the current page for pending candidates.*/
  setCurrentPendingPage: (value: number) => void;
  /* {number} currentPendingPage - The current page number for pending candidates. */
  currentPendingPage: number;
  /* Function to fetch all candidates. */
  getAllCandidates: () => void;
  /** Function to format date to a readable format. */
  formatDateToReadable: (date: string) => string;
  /** Pending per page*/
  setPendingPerPage: (value: number) => void;

  getCandidateQualification: (value: string, alternativePositions: string) => string;
}
/**
 * The `PendingCandidates` component is responsible for rendering a list of candidates
 * awaiting review. It provides functionality to review individual candidates, manage
 * pagination, and adjust the number of items displayed per page. The component also
 * includes a modal for reviewing candidate details.
 *
 * @component
 * @param {AllCandidatesProps} props - The props for the component.
 * @param {CandidatesList} props.candidatesList - The list of candidates with pending reviews.
 * @param {React.Dispatch<React.SetStateAction<number>>} props.setItemPerPageList - Function to set the number of items displayed per page.
 * @param {React.Dispatch<React.SetStateAction<number>>} props.setCurrentPendingPage - Function to set the current page for pending candidates.
 * @param {number} props.currentPendingPage - The current page number for pending candidates.
 * @param {() => void} props.getAllCandidates - Function to fetch all candidates.
 *
 * @returns {JSX.Element} A React component that displays a list of pending candidates, pagination controls, and a modal for reviewing candidate details.
 *
 * @remarks
 * - The component uses Redux for state management, including fetching candidate data and managing the review modal state.
 * - The `handleReviewCandidate` function fetches candidate data and opens the review modal.
 * - Pagination is handled using the `Pagination` component, and the number of items per page can be adjusted via a dropdown.
 * - If no candidates are available for review, a message is displayed instead of the list.
 *
 * @example
 * ```tsx
 * <PendingCandidates
 *   candidatesList={candidatesList}
 *   setItemPerPageList={setItemPerPageList}
 *   setCurrentPendingPage={setCurrentPendingPage}
 *   currentPendingPage={currentPendingPage}
 *   getAllCandidates={getAllCandidates}
 * />
 * ```
 */
const PendingCandidates: React.FC<AllCandidatesProps> = ({
  candidatesList,
  setItemPerPageList,
  setCurrentPendingPage,
  currentPendingPage,
  getAllCandidates,
  formatDateToReadable,
  setPendingPerPage,
  getCandidateQualification
}) => {
  const dispatch = useDispatch();

  const reviewCandidateData = useSelector(
    (state) => state.superadmin.reviewCandidateData
  );

  const [showModal, setShowModal] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<PaginationLink[]>([]);
  const isInitialLoad = useRef<boolean>(false);
  const [currentCandidateId, setCurrentCandidateId] = useState<number>(0);
  const [currentCandidatePrimaryId, setCurrentCandidatePrimaryId] =
    useState<number>(0);
  const [currentCandidateUUID, setCurrentCandidateUUID] = useState<string>("");

  /**
   *
   * @param candidateId - The ID of the candidate to be reviewed.
   * This function fetches the candidate data and opens the review modal.
   */
  const handleReviewCandidate = async (
    candidateId: number,
    candidateUUID: string,
    candidatePrimaryId: number
  ) => {
    setCurrentCandidateId(candidateId);
    setCurrentCandidatePrimaryId(candidatePrimaryId);
    setCurrentCandidateUUID(candidateUUID || "");
    dispatch(showTransparentPreloaderAction());
    // const candidateResponse = await dispatch(
    //   getCandidateData({ candidate_id: candidateUUID })
    // );
    // dispatch(hideTransparentPreloaderAction());
    // if (isFulfilled(candidateResponse)) {
    //   if(reviewCandidateData?.referoo_status === 'complete') {
    //     await dispatch(getReferooDocumentThunk({ user_uuid: candidateUUID ?? "" }));
    //   }
    //   setShowModal(!false);
    // } else {
    //   setShowModal(false);
    //   toast.error("Something went wrong while fetching candidate data.");
    // }
    try {
      const candidateResponse = await dispatch(
        getCandidateData({ candidate_id: candidateUUID })
      );

      if (isFulfilled(candidateResponse)) {
        const referooStatus = candidateResponse.payload?.data?.[0]?.referoo_status;

        if (referooStatus === 'complete' || referooStatus === 'in_progress') {
          const referooResponse = await dispatch(
            getReferooDocumentThunk({ user_uuid: candidateUUID ?? "" })
          );
          // Optional: check referooResponse status here
        }

        setShowModal(true); // Show modal only after all required data is ready
      } else {
        setShowModal(false);
        dispatch(resetReferooDocumentsAction()); // Reset referoo documents on error
        toast.error("Something went wrong while fetching candidate data.");
      }
    } catch (error) {
      setShowModal(false);
      dispatch(resetReferooDocumentsAction()); // Reset referoo documents on error
      toast.error("Unexpected error occurred.");
    } finally {
      dispatch(hideTransparentPreloaderAction()); // End loading
    }
  };

  const handleResendVerificationEmail = async (candidateUUID: string) => {
    dispatch(showTransparentPreloaderAction());
    try {
      const result = await dispatch(resendVerificationEmailThunk({ candidateUuid: candidateUUID }));
      if (isFulfilled(result)) {
        toast.success("Verification email resent successfully.");
      } else {
        toast.error("Failed to resend verification email.");
      }
    } catch {
      toast.error("Unexpected error occurred.");
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  };

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      isInitialLoad.current = false;
      const page = new URL(url).searchParams.get("pending_page");
      setCurrentPendingPage(Number(page));
    }
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (candidatesList?.pending_reviews) {
      setTotalPages(candidatesList?.pending_reviews?.last_page || 1);
      setPaginationLinks(candidatesList?.pending_reviews.links);
    }
  }, [candidatesList?.pending_reviews]);

  const formatDate = (isoDateString: string, reviewPopupModal?: boolean): string => {
    if (!isoDateString) return ""; // Invalid date
    const date = new Date(isoDateString);
    if (isNaN(date.getTime())) return ""; // Invalid Date
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    };

    let finalDate = new Intl.DateTimeFormat("en-GB", options).format(date);
    // if(reviewPopupModal) {
      finalDate = new Intl.DateTimeFormat("en-GB", options)
            .format(date)
            .replace(",", ""); // Remove comma
    // }
    return finalDate;
  };

  return (
    <>
      <div className="d-flex flex-column">
        {candidatesList?.pending_reviews.data?.length > 0 ? (
          candidatesList?.pending_reviews.data?.map((candidate, index: number) => {
            return (
              <div key={index}>
                <div
                  className={`bg-white p-15px d-flex flex-column flex-lg-row gap-20 gap-mobile-10 align-items-lg-center ${
                    candidatesList?.pending_reviews.data?.length > 1
                      ? "border-bottom border-off-white"
                      : ""
                  }`}
                  key={candidate?.id}
                >
                  {candidate?.photo?.length === 0 ? (
                    <PlaceHolderImage />
                  ) : (
                    <div className="w-50px h-50px  radius-50px">
                      <img
                        src={candidate?.photo || DefaultImg}
                        alt="Candidate"
                        width={"50px"}
                        height={"50px"}
                        className="img-responsive rounded-circle object-fit-cover"
                      />
                    </div>
                  )}
                  <div className="flex-grow-1 d-flex flex-column flex-lg-row gap-40 gap-mobile-10 align-items-lg-center justify-content-lg-between">
                    <div className="flex-grow-1">
                      <div className="d-flex gap-20 align-items-center">
                        <div className="flex-grow-1 text-break">
                          <div className="mobile-w-100 d-flex flex-column gap-3 text-black">
                            <div className="fs-16 fw-700">
                              {capitalizeName(candidate?.first_name)} {capitalizeName(candidate?.last_name)}
                            </div>
                            <div className="fs-13 fw-400">
                              {candidate?.positions || "No Positions Selected"}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-10 justify-content-lg-center flex-wrap min-w-350px w-350px mobile-w-100">
                    {(candidate?.review_status === null && candidate.current_stage === null) ? 
                      <>
                        <div className="bg-teal-muted border-1 px-5 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                          {profileSectionTitle?.[1]}
                        </div>
                      </> 
                    : candidate?.profile_steps.length > 0 ? (
                      <>
                        {(candidate?.profile_steps?.[0]?.step_number ?? 0) +
                          1 !==
                          4 && (
                          <div className="bg-teal-muted border-1 px-5 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                            {
                              profileSectionTitle?.[
                                (
                                  (candidate?.profile_steps?.[0]?.step_number ??
                                    0) + 1
                                ).toString() as keyof typeof profileSectionTitle
                              ]
                            }
                          </div>
                        )}
                        {
                          /**
                           * Show Pending Review status if the candidate is not approved and the step number is not 3, 
                           * or if the step number is 1 and the action is to approve candidate bypass compliance check (Second option choosed in radio button)
                           * or if the step number is 1 and the action is to approve candidate to stage 2 and referoo status is complete. (First option choosed in radio button and check candidate referoo status)
                           */
                        }
                        {( 
                          candidate?.review_status !== "reopened" && (
                            (candidate?.profile_steps?.[0]?.step_number !== 3 && candidate?.review_status !== "approved") ||
                            (candidate?.profile_steps?.[0]?.step_number === 1 && candidate?.profile_steps?.[0]?.action === "approve_candidate_bypass_compliance_check") ||
                            (candidate?.profile_steps?.[0]?.step_number === 1 && candidate?.profile_steps?.[0]?.action === "approve_candidate_to_stage_2" && candidate?.referoo_status === "complete")
                          ) && (
                            <div className="bg-white border-1 px-5 border-solid border-alert radius-8px min-w-70px text-center text-alert fs-13 fw-400 lh-22px">
                              Pending Review
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      <>
                        <div className="bg-teal-muted border-1 px-5 border-solid border-black radius-8px min-w-70px text-center fs-13 fw-400 lh-22px">
                          {profileSectionTitle?.[1]}
                        </div>
                        {candidate?.review_status !== "reopened" && (
                          <div className="bg-white border-1 px-5 border-solid border-alert radius-8px min-w-70px text-center text-alert fs-13 fw-400 lh-22px">
                            Pending Review
                          </div>
                        )}
                      </>
                    )}
                    {
                      candidate?.email_verified_at === null && (
                        <div className="bg-white border-1 px-5 border-solid border-alert radius-8px min-w-70px text-center text-alert fs-13 fw-400 lh-22px">
                          Email not verified <button type="button" onClick={() => handleResendVerificationEmail(candidate?.uuid)} className="border-0 bg-transparent p-0 text-decoration-underline text-alert fw-700">{"Resend Mail"}</button>
                        </div>
                      )
                    }

                    {candidate?.has_pending_edit_request && (
                      <div className="bg-white border-1 px-5 border-solid border-alert radius-8px min-w-70px text-center text-alert fs-13 fw-400 lh-22px">
                        Request to Edit Profile
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-3 min-w-130px w-130px mobile-w-100">
                    <div className="fs-10 fw-400 text-grey-medium">
                      { (candidate?.current_stage !== null && candidate?.review_status !== null) ? `Last Submission Date` : `Date Started` }
                    </div>
                    <div className="fs-13 fw-400 text-black">
                      { (candidate?.current_stage !== null && candidate?.review_status !== null) ? formatDate(candidate?.updated_at ?? "") : formatDate(candidate?.created_at ?? "") }
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-3 min-w-180px w-180px mobile-w-100">
                    <div className="fs-10 fw-400 text-grey-medium">
                      Lives in
                    </div>
                    <div
                      className="fs-13 fw-400 text-black lh-14px text-truncate"
                      title={candidate?.address}
                    >
                      {/* {candidate?.address?.length > 10
                          ? `${candidate?.address.slice(0, 20)}...`
                          : candidate?.address} */}
                      {}
                      {extractSuburbFromAddress(candidate?.address ?? "")}
                    </div>
                  </div>
                  <div className="min-w-126px">
                    <button
                      type="button"
                      className="btn-theme bg-teal text-black mobile-w-100"
                      onClick={() =>
                        handleReviewCandidate(
                          candidate?.id,
                          candidate?.uuid,
                          candidate?.candidate_primary_key
                        )
                      }
                    >
                      Review
                      <svg
                        width="24"
                        height="25"
                        viewBox="0 0 24 25"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M14.1924 19.2071C13.8018 18.8166 13.8018 18.1834 14.1924 17.7929L18.4852 13.5L2.89942 13.5C2.34713 13.5 1.89942 13.0523 1.89942 12.5C1.89942 11.9477 2.34713 11.5 2.89942 11.5L18.4852 11.5L14.1924 7.2071C13.8018 6.81658 13.8018 6.18341 14.1924 5.79289C14.5829 5.40237 15.2161 5.40237 15.6066 5.7929L21.6065 11.7929C21.997 12.1834 21.997 12.8166 21.6065 13.2071L15.6066 19.2071C15.2161 19.5976 14.5829 19.5976 14.1924 19.2071Z"
                          fill="black"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white p-15px d-flex flex-column flex-lg-row gap-40 gap-mobile-20 align-items-start align-items-lg-center justify-content-between">
            <div className="flex-grow-1">
              <div className="d-flex gap-20 align-items-center">
                <div className="flex-grow-1 text-break">
                  <div className="mobile-w-100 d-flex flex-column gap-3 text-black">
                    <div className="fs-16 text-center text-grey-medium">
                      No Pending Review Candidates
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Pagination Component */}
      {candidatesList?.pending_reviews.data?.length > 0 ? (
        <div className="pt-10px">
          <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap">
            <div className="d-flex gap-3 align-items-center">
              <div className="fs-13 text-black text-nowrap">Items per page</div>
              <div className="form-group">
                <select
                  name=""
                  id=""
                  className="form-select text-only"
                  onChange={(e) => {
                    setPendingPerPage(Number(e.target.value));
                    setCurrentPendingPage(1); // If we are in page 2 and we change the items per page to 10, we need to go back to page 1.
                  }}
                >
                  {ITEMS_PER_PAGE_OPTIONS?.map((item, index) => {
                    return (
                      <option key={index} value={item}>
                        {item}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <Pagination
              currentPage={currentPendingPage}
              totalPages={totalPages}
              paginationLinks={paginationLinks}
              maxVisiblePages={MAX_VISIBLE_PAGES}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      ) : (
        <></>
      )}

      {/* Candidate Review Modal */}
      {reviewCandidateData && showModal && (
        <CandidateReviewModal
          showModal={showModal}
          setShowModal={setShowModal}
          currentCandidateId={currentCandidateId}
          reviewCandidateData={reviewCandidateData}
          getAllCandidates={getAllCandidates}
          handleReviewCandidate={handleReviewCandidate}
          currentCandidateUUID={currentCandidateUUID}
          currentCandidatePrimaryId={currentCandidatePrimaryId}
          getCandidateQualification={getCandidateQualification}
        />
      )}
    </>
  );
};

export default PendingCandidates;
